"use client";

/**
 * usePredictPositionsQuery - TanStack Query hook for Predict.fun positions
 *
 * Centralizes all fetching of /api/trading/predict/positions behind a single cache key.
 * Multiple components using this hook share the same request (deduplication).
 * Invalidates automatically on "predict:refresh" window event.
 */

import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { authFetch } from "@/lib/authFetch";

export interface PredictPosition {
  marketId: string;
  marketTitle: string;
  marketSlug: string;
  image: string | null;
  outcome: string;
  outcomeIndex: number;
  size: number;
  currentPrice: number;
  avgPrice: number;
  valueUsd: number;   // Total position value from Predict.fun API
  pnlUsd: number;     // P&L from Predict.fun API
  isOpen: boolean;
  conditionId: string | null;
  isNegRisk?: boolean;
  isYieldBearing?: boolean;
}

const QUERY_KEY = ["predict", "positions"] as const;

export function usePredictPositionsQuery(enabled = true) {
  const { isLoggedIn } = useAuth();
  const queryClient = useQueryClient();

  // Invalidate cache on trade events (both predict:refresh and generic positions:refresh)
  useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      // Refetch after a delay to account for BSC block confirmation
      setTimeout(() => queryClient.invalidateQueries({ queryKey: QUERY_KEY }), 5000);
    };
    window.addEventListener("predict:refresh", handler);
    window.addEventListener("positions:refresh", handler);
    return () => {
      window.removeEventListener("predict:refresh", handler);
      window.removeEventListener("positions:refresh", handler);
    };
  }, [queryClient]);

  return useQuery<PredictPosition[]>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await authFetch("/api/trading/predict/positions");
      if (!res.ok) return [];
      const data = await res.json();
      if (data.success && Array.isArray(data.positions)) {
        return data.positions.filter((p: PredictPosition) => p.isOpen && p.size > 0.001);
      }
      return [];
    },
    enabled: isLoggedIn && enabled,
    staleTime: 30_000,
  });
}

/** Derived hook: portfolio summary for Predict.fun */
export function usePredictPortfolioSummary() {
  const { data: positions, isLoading } = usePredictPositionsQuery();

  // Portfolio value = sum of valueUsd from Predict.fun API (more accurate than calculating ourselves)
  const portfolioValue = positions
    ? positions.reduce((sum, p) => sum + (Number(p.valueUsd) || 0), 0)
    : null;

  // Total P&L from API
  const totalPnl = positions
    ? positions.reduce((sum, p) => sum + (Number(p.pnlUsd) || 0), 0)
    : null;

  return {
    portfolioValue,
    totalPnl,
    positionCount: positions?.length ?? 0,
    loading: isLoading,
  };
}

/** Invalidate positions cache from outside React (e.g., after trade execution) */
export function invalidatePredictPositions(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: QUERY_KEY });
}
